import { forgetAllSorts } from './gridSort'
import { useEffect } from 'react'
import type { Api } from '../api'
import {
  configureGridLayoutStore,
  forgetAllLayouts,
  restore,
  type GridLayout,
} from './gridLayout'

/**
 * DURABLE GRID LAYOUTS — the half of saved views that has no user interface.
 *
 * A reader hides six columns, drags Customer to the front and widens one, and
 * until 24 Sep 2026 all of it died on the next page load: `gridLayout.tsx` kept
 * it in a module-level Map. This is the attachment that file has named since
 * 18 Sep in `snapshot()` and `restore()`, wired to
 * `db/platform/036_reader_preferences.sql`.
 *
 * Nothing here is visible. A reader who never thinks about any of it simply
 * finds their columns where they left them.
 *
 * WHAT THIS FILE OWNS, and why it is not in `gridLayout.tsx`: the debounce, the
 * once-per-session load, and the API client. `gridLayout.tsx` imports nothing
 * but React and is imported by `ui.tsx` and `DataGrid.tsx`; an API import from
 * there would close a cycle that resolves to `undefined` at point of use rather
 * than failing at build.
 */

/**
 * Long enough that a drag is one write, short enough that closing the tab
 * straight after arranging a grid still saves it.
 *
 * `write()` fires on every frame of a column drag — a 400px drag is dozens of
 * calls. Without this, arranging one grid would be a write per frame against a
 * table keyed on the reader.
 */
const DEBOUNCE_MS = 800

/** The payload shape this build writes. The server stores it and never reads it. */
const SCHEMA_VERSION = 1

/**
 * What goes on the wire, which is NOT the whole `GridLayout`.
 *
 * `headers` is deliberately absent. It exists so `applyLayout()` can match the
 * extract builders' column arrays, which carry no key — and the live columns
 * already know their own header text. A stored copy would roughly double the
 * payload and go stale the day somebody rewords a column, which would break
 * extracts silently: the worst failure this file can cause.
 */
interface StoredLayout {
  order: string[]
  hidden: string[]
  widths: Record<string, number>
  pinned: Record<string, 'left' | 'right'>
}

function toStored(layout: GridLayout): StoredLayout {
  return {
    order: layout.order,
    hidden: layout.hidden,
    widths: layout.widths,
    pinned: layout.pinned,
  }
}

/**
 * Back into a `GridLayout`, defensively.
 *
 * Every field is checked, because this string came from a database and could
 * have been written by an older build, a newer one, or a bug. Anything that is
 * not the shape it claims returns null and the grid uses its own defaults —
 * never an exception, which would take the report down over a column order.
 * `MeasureSource.Parse` in the API takes the same line for the same reason.
 */
function fromStored(json: string, headers: Record<string, string>): GridLayout | null {
  try {
    const raw = JSON.parse(json) as Partial<StoredLayout>
    if (!Array.isArray(raw.order) || !raw.order.every((k) => typeof k === 'string')) return null
    const hidden = Array.isArray(raw.hidden) ? raw.hidden.filter((k) => typeof k === 'string') : []
    const widths: Record<string, number> = {}
    for (const [k, v] of Object.entries(raw.widths ?? {})) {
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) widths[k] = v
    }
    const pinned: Record<string, 'left' | 'right'> = {}
    for (const [k, v] of Object.entries(raw.pinned ?? {})) {
      if (v === 'left' || v === 'right') pinned[k] = v
    }
    return { order: raw.order, hidden, widths, pinned, headers }
  } catch {
    return null
  }
}

/**
 * Attaches durable storage for as long as this component is mounted.
 *
 * Mounted once, from `App`, above every report.
 *
 * `identity` is whoever the session currently is — the person being acted as,
 * under Login As. Changing it reloads from scratch AND empties what is already
 * in memory: the module store survives an identity switch, so without that an
 * administrator's own column order would follow them into the screen of the
 * person they are acting as, and then be saved there.
 */
export function useDurableGridLayouts(api: Api, identity: string | null): void {
  useEffect(() => {
    let cancelled = false
    const timers = new Map<string, ReturnType<typeof setTimeout>>()
    const pending = new Map<string, StoredLayout>()

    const flush = (gridId: string) => {
      const payload = pending.get(gridId)
      if (!payload) return
      pending.delete(gridId)
      const timer = timers.get(gridId)
      if (timer) {
        clearTimeout(timer)
        timers.delete(gridId)
      }
      /* QUIET ON FAILURE, always. This is decoration on a report that works
         without it; a column order that failed to save must never become a
         panel in front of the figures. */
      void api.saveGridLayout(gridId, JSON.stringify(payload)).catch(() => {})
    }

    configureGridLayoutStore({
      onChange(gridId, layout) {
        pending.set(gridId, toStored(layout))
        const existing = timers.get(gridId)
        if (existing) clearTimeout(existing)
        timers.set(gridId, setTimeout(() => flush(gridId), DEBOUNCE_MS))
      },
      onForget(gridId) {
        // Drop anything queued first: a debounced write landing after the
        // forget would put the row straight back.
        pending.delete(gridId)
        const timer = timers.get(gridId)
        if (timer) {
          clearTimeout(timer)
          timers.delete(gridId)
        }
        void api.forgetGridLayout(gridId).catch(() => {})
      },
    })

    /* A TAB CLOSED MID-DEBOUNCE STILL SAVES. Eight hundred milliseconds is
       easily short enough to feel instant and easily long enough to lose a
       drag to somebody closing the tab straight afterwards. `pagehide` is the
       event that actually fires on mobile Safari, where `beforeunload` does
       not; `visibilitychange` covers switching away without closing. */
    const flushAll = () => {
      for (const gridId of [...pending.keys()]) flush(gridId)
    }
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushAll()
    }
    window.addEventListener('pagehide', flushAll)
    document.addEventListener('visibilitychange', onHidden)

    /* A different person: what is in memory belongs to the previous identity.
       The sorts go with them -- a sort is session state that a saved view can
       carry, and one left behind would be the last reader's question sitting
       over this one's rows. */
    forgetAllLayouts()
    forgetAllSorts()

    void api
      .gridLayouts()
      .then((rows) => {
        if (cancelled) return
        for (const row of rows) {
          if (row.schemaVersion !== SCHEMA_VERSION) continue
          /* Headers come from the live grid, so an empty map here is correct:
             `reconcile` replaces it with the real one on the first render that
             sees this layout. */
          const layout = fromStored(row.layout, {})
          if (layout) restore(row.gridId, layout)
        }
      })
      .catch(() => {
        /* No stored layouts is exactly how the platform behaved before this
           existed, so a failed load is a silent return to that. */
      })

    return () => {
      cancelled = true
      flushAll()
      window.removeEventListener('pagehide', flushAll)
      document.removeEventListener('visibilitychange', onHidden)
      for (const timer of timers.values()) clearTimeout(timer)
      configureGridLayoutStore(null)
    }
  }, [api, identity])
}
